import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clearTokens, getCurrentUserProfile, getProfilePictureUrl } from '../services/api';
import EmployeeImputationsPage from './EmployeeImputationsPage';
import EmployeePerformanceComparisonPage from './EmployeePerformanceComparisonPage';
import RewardsPage from './RewardsPage';
import BadgeRulesPage from './BadgeRulesPage';
import EmployeeTeamPage from './EmployeeTeamPage';
import EmployeeWeeklyViewPage from './EmployeeWeeklyViewPage';
import EmployeeMonthViewPage from './EmployeeMonthViewPage';
import EmployeeScheduleViewPage from './EmployeeScheduleViewPage';
import EmployeeSidebar from './EmployeeSidebar';
import AdminTopBar from './AdminTopBar';
import NotificationSidebar from './NotificationSidebar';
import { getCurrentWeekLabel, toIsoDateLocal, parseIsoDateLocal, startOfWeekMonday } from './weeklyView/constants';
import { getCurrentMonthLabel } from './monthView/constants';
import { getCurrentScheduleLabel } from './scheduleView/constants';

const CALENDAR_MODE_KEY = 'employeeCalendarMode';
const MENU_ITEM_KEY = 'employeeActiveMenuItem';
const DASHBOARD_BASE = '/employee-dashboard';

const ALLOWED_MENU_ITEMS = [
  'imputations',
  'performance-comparison',
  'rewards',
  'badge-rules',
  'team',
  'weekly-view',
];

const SECTION_PATHS = {
  imputations: `${DASHBOARD_BASE}/imputations`,
  'performance-comparison': `${DASHBOARD_BASE}/performance-comparison`,
  rewards: `${DASHBOARD_BASE}/rewards`,
  'badge-rules': `${DASHBOARD_BASE}/badge-rules`,
  team: `${DASHBOARD_BASE}/team`,
};

const getSavedCalendarMode = () => {
  try {
    const saved = localStorage.getItem(CALENDAR_MODE_KEY);
    if (saved === 'month' || saved === 'schedule') return saved;
    return 'week';
  } catch (error) {
    return 'week';
  }
};

const getSavedMenuItem = () => {
  try {
    const savedMenuItem = localStorage.getItem(MENU_ITEM_KEY);
    return ALLOWED_MENU_ITEMS.includes(savedMenuItem) ? savedMenuItem : 'imputations';
  } catch (error) {
    return 'imputations';
  }
};

const planningPath = (mode, weekStartIso = null) => {
  if (mode === 'week' && weekStartIso) {
    return `${DASHBOARD_BASE}/planning/week/${weekStartIso}`;
  }
  return `${DASHBOARD_BASE}/planning/${mode}`;
};

const pathForMenuItem = (menuItem, calendarMode = 'week', weekStartIso = null) => {
  if (menuItem === 'weekly-view') {
    const mode =
      calendarMode === 'month' || calendarMode === 'schedule' ? calendarMode : 'week';
    return planningPath(mode, mode === 'week' ? weekStartIso : null);
  }
  return SECTION_PATHS[menuItem] || SECTION_PATHS.imputations;
};

const parseEmployeePath = (pathname) => {
  if (pathname === DASHBOARD_BASE || pathname === `${DASHBOARD_BASE}/`) {
    return null;
  }

  const weekDateMatch = pathname.match(
    /^\/employee-dashboard\/planning\/week\/(\d{4}-\d{2}-\d{2})\/?$/
  );
  if (weekDateMatch) {
    const parsedDate = parseIsoDateLocal(weekDateMatch[1]);
    if (!parsedDate) {
      return { menuItem: 'weekly-view', calendarMode: 'week', weekStartIso: null, invalidWeekDate: true };
    }
    const mondayIso = toIsoDateLocal(startOfWeekMonday(parsedDate));
    return {
      menuItem: 'weekly-view',
      calendarMode: 'week',
      weekStartIso: mondayIso,
      needsWeekCanonicalRedirect: mondayIso !== weekDateMatch[1],
    };
  }

  const planningMatch = pathname.match(/^\/employee-dashboard\/planning\/(week|month|schedule)\/?$/);
  if (planningMatch) {
    return {
      menuItem: 'weekly-view',
      calendarMode: planningMatch[1],
      weekStartIso: null,
    };
  }

  if (pathname === `${DASHBOARD_BASE}/planning` || pathname === `${DASHBOARD_BASE}/planning/`) {
    return {
      menuItem: 'weekly-view',
      calendarMode: getSavedCalendarMode(),
      weekStartIso: null,
    };
  }

  for (const [menuItem, path] of Object.entries(SECTION_PATHS)) {
    if (pathname === path || pathname === `${path}/`) {
      return { menuItem, calendarMode: null, weekStartIso: null };
    }
  }

  return undefined;
};

const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('nav');
  const { t: tEmployee } = useTranslation('employee');
  const { t: tCommon } = useTranslation('common');
  const weeklyViewRef = useRef(null);
  const monthViewRef = useRef(null);
  const scheduleViewRef = useRef(null);
  const [weekRangeLabel, setWeekRangeLabel] = useState(getCurrentWeekLabel);
  const [monthRangeLabel, setMonthRangeLabel] = useState(getCurrentMonthLabel);
  const [scheduleRangeLabel, setScheduleRangeLabel] = useState(getCurrentScheduleLabel);
  const [calendarSearch, setCalendarSearch] = useState('');

  const parsedPath = parseEmployeePath(location.pathname);
  const [calendarMode, setCalendarMode] = useState(() => {
    if (parsedPath?.calendarMode) return parsedPath.calendarMode;
    return getSavedCalendarMode();
  });
  const [activeMenuItem, setActiveMenuItem] = useState(() => {
    if (parsedPath?.menuItem) return parsedPath.menuItem;
    return getSavedMenuItem();
  });
  const [weekStartIso, setWeekStartIso] = useState(() => parsedPath?.weekStartIso || null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  
  // Current user data state
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const isCalendarView = activeMenuItem === 'weekly-view';
  const isMonthMode = isCalendarView && calendarMode === 'month';
  const isWeekMode = isCalendarView && calendarMode === 'week';
  const isScheduleMode = isCalendarView && calendarMode === 'schedule';

  const callWeekly = useCallback((method, ...args) => {
    const api = weeklyViewRef.current;
    if (api && typeof api[method] === 'function') {
      return api[method](...args);
    }
    return undefined;
  }, []);

  const callMonth = useCallback((method, ...args) => {
    const api = monthViewRef.current;
    if (api && typeof api[method] === 'function') {
      return api[method](...args);
    }
    return undefined;
  }, []);

  const callSchedule = useCallback((method, ...args) => {
    const api = scheduleViewRef.current;
    if (api && typeof api[method] === 'function') {
      return api[method](...args);
    }
    return undefined;
  }, []);

  const callCalendar = useCallback((weekMethod, monthMethod, scheduleMethod, ...args) => {
    if (calendarMode === 'schedule') {
      return callSchedule(scheduleMethod, ...args);
    }
    if (calendarMode === 'month') {
      return callMonth(monthMethod, ...args);
    }
    return callWeekly(weekMethod, ...args);
  }, [calendarMode, callMonth, callWeekly, callSchedule]);

  const syncCalendarSearch = useCallback((value) => {
    setCalendarSearch(value);
    callCalendar('setSearchQuery', 'setSearchQuery', 'setSearchQuery', value);
  }, [callCalendar]);

  const handleMenuItemClick = (itemId) => {
    const nextPath = pathForMenuItem(itemId, calendarMode, weekStartIso);
    if (location.pathname !== nextPath) {
      navigate(nextPath);
    }
  };

  const handleMenuClick = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleCloseMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleSignOut = () => {
    clearTokens();
    navigate('/login', { replace: true });
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleCalendarModeChange = (mode) => {
    if (mode !== 'week' && mode !== 'month' && mode !== 'schedule') return;
    const nextPath = planningPath(mode, mode === 'week' ? weekStartIso : null);
    if (location.pathname !== nextPath) {
      navigate(nextPath);
    }
  };

  const handleWeekStartChange = useCallback(
    (mondayIso) => {
      if (!mondayIso) return;
      setWeekStartIso(mondayIso);
      const nextPath = planningPath('week', mondayIso);
      if (location.pathname === nextPath) return;
      const isBareWeek = /^\/employee-dashboard\/planning\/week\/?$/.test(location.pathname);
      navigate(nextPath, { replace: isBareWeek });
    },
    [location.pathname, navigate]
  );

  // Sync menu/calendar from URL; redirect bare or invalid paths
  useEffect(() => {
    const parsed = parseEmployeePath(location.pathname);

    if (parsed === null) {
      const savedMenu = getSavedMenuItem();
      const savedMode = getSavedCalendarMode();
      navigate(pathForMenuItem(savedMenu, savedMode, weekStartIso), { replace: true });
      return;
    }

    if (parsed === undefined) {
      navigate(SECTION_PATHS.imputations, { replace: true });
      return;
    }

    if (parsed.invalidWeekDate) {
      navigate(planningPath('week'), { replace: true });
      return;
    }

    if (parsed.needsWeekCanonicalRedirect && parsed.weekStartIso) {
      navigate(planningPath('week', parsed.weekStartIso), { replace: true });
      return;
    }

    if (
      parsed.menuItem === 'weekly-view' &&
      /^\/employee-dashboard\/planning\/?$/.test(location.pathname)
    ) {
      navigate(planningPath(parsed.calendarMode, parsed.calendarMode === 'week' ? weekStartIso : null), {
        replace: true,
      });
      return;
    }

    setActiveMenuItem(parsed.menuItem);
    if (parsed.calendarMode) {
      setCalendarMode(parsed.calendarMode);
    }
    if (parsed.calendarMode === 'week') {
      setWeekStartIso(parsed.weekStartIso || null);
    }
  }, [location.pathname, navigate]); // weekStartIso intentionally omitted — used only for bare /planning redirect

  // Fetch current user data on mount
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        setLoadingUser(true);
        const userData = await getCurrentUserProfile();
        setCurrentUser(userData);
        
        // Check if employee has a team - if not, redirect to profile
        if (userData.role === 'EMPLOYE' && (!userData.team || userData.team === null)) {
          navigate('/profile', { replace: true });
        }
      } catch (error) {
        console.error('Failed to fetch current user:', error);
        // Set default values if fetch fails
        setCurrentUser({
          first_name: '',
          last_name: '',
          email: '',
          role: 'EMPLOYE'
        });
      } finally {
        setLoadingUser(false);
      }
    };

    fetchCurrentUser();
  }, [navigate]);

  useEffect(() => {
    try {
      localStorage.setItem(MENU_ITEM_KEY, activeMenuItem);
    } catch (error) {
      // Ignore storage write failures (private mode/quota)
    }
  }, [activeMenuItem]);

  useEffect(() => {
    try {
      localStorage.setItem(CALENDAR_MODE_KEY, calendarMode);
    } catch (error) {
      // Ignore storage write failures
    }
  }, [calendarMode]);

  useEffect(() => {
    if (!isCalendarView) return;
    callCalendar('setSearchQuery', 'setSearchQuery', 'setSearchQuery', calendarSearch);
  }, [isCalendarView, calendarMode, calendarSearch, callCalendar]);

  // Get user display name
  const userName = currentUser 
    ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.email || t('employeeDefault')
    : t('employeeDefault');
  
  const userSubtitle = currentUser?.team?.name || t('employeeDefault');
  const userProfileImage = currentUser?.profile_picture 
    ? getProfilePictureUrl(currentUser.profile_picture)
    : null;
  
  // Calculate initials from first name and last name
  const userInitials = currentUser 
    ? (currentUser.first_name && currentUser.last_name
        ? `${currentUser.first_name[0]}${currentUser.last_name[0]}`.toUpperCase()
        : currentUser.first_name 
          ? currentUser.first_name[0].toUpperCase()
          : currentUser.email 
            ? currentUser.email[0].toUpperCase()
            : 'E')
    : 'E';

  // Get page title based on active menu item
  const getPageTitle = (menuItem) => {
    const titleMap = {
      'imputations': t('pageTitles.imputations'),
      'performance-comparison': t('pageTitles.performance-comparison'),
      'rewards': t('pageTitles.rewards-catalog'),
      'badge-rules': t('pageTitles.badge-calculation-rules'),
      'team': t('pageTitles.team'),
      'weekly-view': t('pageTitles.calendar'),
    };
    return titleMap[menuItem] || t('pageTitles.imputations');
  };

  const rangeLabel = isScheduleMode
    ? scheduleRangeLabel
    : isMonthMode
      ? monthRangeLabel
      : weekRangeLabel;

  const calendarLeftExtra = (
    <AnimatePresence mode="wait">
      {isCalendarView ? (
        <motion.div
          key="calendar-left-extra"
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -8 }}
          transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
          className="flex items-center gap-3 min-w-0 flex-1"
        >
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              className="p-2 text-text-secondary hover:text-primary hover:bg-background-light rounded-lg transition-colors"
              aria-label={isMonthMode || isScheduleMode ? tEmployee('dashboard.previousMonth') : tEmployee('dashboard.previousDays')}
              onClick={() => callCalendar('goPrevDays', 'goPrevMonth', 'goPrevMonth')}
            >
              <span className="material-symbols-outlined">chevron_left</span>
            </button>
            <span className="text-sm font-semibold text-text-main whitespace-nowrap hidden sm:inline">
              {rangeLabel}
            </span>
            <button
              type="button"
              className="p-2 text-text-secondary hover:text-primary hover:bg-background-light rounded-lg transition-colors"
              aria-label={isMonthMode || isScheduleMode ? tEmployee('dashboard.nextMonth') : tEmployee('dashboard.nextDays')}
              onClick={() => callCalendar('goNextDays', 'goNextMonth', 'goNextMonth')}
            >
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
          <div className="relative w-full max-w-xs hidden lg:block">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary text-[18px]">
              search
            </span>
            <input
              className="w-full pl-9 pr-3 py-1.5 bg-surface border border-border rounded-lg text-sm text-text-main placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              placeholder={tEmployee('dashboard.searchEvents')}
              type="text"
              value={calendarSearch}
              onChange={(e) => syncCalendarSearch(e.target.value)}
            />
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  const pillActive = 'px-3 py-1 text-xs font-bold bg-surface shadow-sm rounded-md text-primary';
  const pillIdle = 'px-3 py-1 text-xs font-medium text-text-secondary hover:text-text-main transition-colors rounded-md';

  const calendarActions = (
    <AnimatePresence mode="wait">
      {isCalendarView ? (
        <motion.div
          key="calendar-actions"
          initial={{ opacity: 0, x: 8 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 8 }}
          transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
          className="flex items-center gap-3"
        >
          <div className="hidden md:flex gap-1 bg-background-light p-1 rounded-lg border border-border">
            <button
              type="button"
              className={calendarMode === 'week' ? pillActive : pillIdle}
              onClick={() => handleCalendarModeChange('week')}
            >
              Week
            </button>
            <button
              type="button"
              className={calendarMode === 'month' ? pillActive : pillIdle}
              onClick={() => handleCalendarModeChange('month')}
            >
              Month
            </button>
            <button
              type="button"
              className={calendarMode === 'schedule' ? pillActive : pillIdle}
              onClick={() => handleCalendarModeChange('schedule')}
            >
              Schedule
            </button>
          </div>
          {isWeekMode ? (
            <button
              type="button"
              data-weekly-groups-toggle
              className="flex items-center gap-1.5 px-3 py-1.5 bg-surface border border-border hover:border-primary/40 text-text-main rounded-lg text-xs font-medium transition-all"
              onClick={() => callWeekly('toggleGroupsMenu')}
            >
              <span className="material-symbols-outlined text-[18px] text-primary">folder_copy</span>
              <span className="hidden sm:inline">Groups</span>
              <span className="material-symbols-outlined text-[16px] text-text-secondary hidden sm:inline">expand_more</span>
            </button>
          ) : null}
          {isWeekMode ? (
            <button
              type="button"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-surface border border-border hover:border-primary/40 text-text-main rounded-lg text-xs font-medium transition-all"
              onClick={() => callWeekly('openImportOutlook')}
            >
              <span className="material-symbols-outlined text-[18px] text-primary">event_available</span>
              <span className="hidden sm:inline">Import Outlook</span>
            </button>
          ) : null}
          <button
            type="button"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary hover:opacity-90 text-white rounded-lg text-xs font-medium active:scale-[0.98] transition-all"
            onClick={() => callCalendar('openCreateEvent', 'openCreateEvent', 'openCreateEvent')}
          >
            <span className="material-symbols-outlined text-[18px]">add</span>
            <span className="hidden sm:inline">Create Event</span>
          </button>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  return (
    <div className="bg-background text-text-main overflow-hidden h-screen flex">
      <EmployeeSidebar
        activeMenuItem={activeMenuItem}
        onMenuItemClick={handleMenuItemClick}
        onSignOut={handleSignOut}
        onProfileClick={handleProfileClick}
        userName={userName}
        userSubtitle={userSubtitle}
        userProfileImage={userProfileImage}
        userInitials={userInitials}
        isMobileOpen={isMobileMenuOpen}
        onClose={handleCloseMobileMenu}
        showWeeklyView
      />
      <main className="flex-1 min-w-0 flex flex-col h-full overflow-hidden bg-background relative">
        <AdminTopBar
          title={getPageTitle(activeMenuItem)}
          searchPlaceholder={tCommon('search')}
          onSearch={() => {}}
          onMenuClick={handleMenuClick}
          onHelpClick={() => {}}
          onSettingsClick={() => {}}
          showSearch={false}
          showMenuButton={true}
          points={currentUser?.total_points}
          isNotificationOpen={isNotificationOpen}
          onNotificationToggle={() => setIsNotificationOpen(prev => !prev)}
          notificationUnreadCount={notificationUnreadCount}
          leftExtra={calendarLeftExtra}
          actions={calendarActions}
        />
        <div
          className={
            isCalendarView
              ? 'flex-1 overflow-hidden p-0 min-h-0 relative'
              : 'flex-1 overflow-y-auto p-8 scroll-smooth relative'
          }
        >
          <AnimatePresence mode="wait">
            {isWeekMode ? (
              <motion.div
                key="weekly-view"
                initial={{ opacity: 0, y: 20, scale: 0.985 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -16, scale: 0.985 }}
                transition={{
                  duration: 0.32,
                  ease: [0.4, 0, 0.2, 1],
                }}
                className="h-full min-h-0 w-full"
              >
                <EmployeeWeeklyViewPage
                  ref={weeklyViewRef}
                  weekStartIso={weekStartIso}
                  onWeekStartChange={handleWeekStartChange}
                  onWeekRangeChange={setWeekRangeLabel}
                />
              </motion.div>
            ) : isMonthMode ? (
              <motion.div
                key="month-view"
                initial={{ opacity: 0, y: 20, scale: 0.985 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -16, scale: 0.985 }}
                transition={{
                  duration: 0.32,
                  ease: [0.4, 0, 0.2, 1],
                }}
                className="h-full min-h-0 w-full"
              >
                <EmployeeMonthViewPage
                  ref={monthViewRef}
                  onMonthRangeChange={setMonthRangeLabel}
                />
              </motion.div>
            ) : isScheduleMode ? (
              <motion.div
                key="schedule-view"
                initial={{ opacity: 0, y: 20, scale: 0.985 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -16, scale: 0.985 }}
                transition={{
                  duration: 0.32,
                  ease: [0.4, 0, 0.2, 1],
                }}
                className="h-full min-h-0 w-full"
              >
                <EmployeeScheduleViewPage
                  ref={scheduleViewRef}
                  onScheduleRangeChange={setScheduleRangeLabel}
                />
              </motion.div>
            ) : (
              <motion.div
                key={activeMenuItem}
                initial={{ opacity: 0, y: 20, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.98 }}
                transition={{
                  duration: 0.3,
                  ease: [0.4, 0, 0.2, 1],
                }}
                className="mx-auto flex flex-col gap-8"
              >
                {activeMenuItem === 'imputations' ? (
                  <EmployeeImputationsPage />
                ) : activeMenuItem === 'performance-comparison' ? (
                  <EmployeePerformanceComparisonPage />
                ) : activeMenuItem === 'rewards' ? (
                  <RewardsPage />
                ) : activeMenuItem === 'badge-rules' ? (
                  <BadgeRulesPage />
                ) : activeMenuItem === 'team' ? (
                  <EmployeeTeamPage />
                ) : (
                  <div className="text-center py-12">
                    <h1 className="text-4xl font-bold text-gray-900">Hello World</h1>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      <NotificationSidebar
        isOpen={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
        onUnreadCountChange={setNotificationUnreadCount}
      />
    </div>
  );
};

export default EmployeeDashboard;
